import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import Check from "@mui/icons-material/Check";
import { Box, ListItemText, Menu, MenuItem, Tooltip } from "@mui/material";
import { useId, useState } from "react";

import { SkillAccessChip } from "../SkillAccess/SkillAccessChip";
import { type SkillAccessToken } from "../SkillAccess/skillAccessTokens";

type SkillChipSelectProps<T extends string> = {
  /** Field name, shown as the chip's tooltip and used for its accessible name. */
  label: string;
  /** The current value's token. */
  value: SkillAccessToken<T>;
  options: readonly SkillAccessToken<T>[];
  onChange: (value: T) => void;
  disabled?: boolean;
};

/** A grid-style chip that opens a menu of the other chips to pick from. */
export function SkillChipSelect<T extends string>({
  label,
  value,
  options,
  onChange,
  disabled = false,
}: SkillChipSelectProps<T>) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const menuId = useId();
  const open = Boolean(anchorEl);
  const hasChoice = options.some((option) => option.value !== value.value);

  return (
    <>
      <Tooltip title={label}>
        {disabled || !hasChoice ? (
          <span>
            <SkillAccessChip token={value} />
          </span>
        ) : (
          <SkillAccessChip
            token={value}
            label={
              <Box
                component="span"
                sx={{ display: "inline-flex", alignItems: "center", mr: -0.75 }}
              >
                {value.label}
                <ArrowDropDown aria-hidden sx={{ fontSize: 18 }} />
              </Box>
            }
            onClick={(event) => setAnchorEl(event.currentTarget)}
            aria-label={`${label}: ${value.label}`}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls={open ? menuId : undefined}
          />
        )}
      </Tooltip>
      <Menu
        id={menuId}
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        MenuListProps={{ role: "listbox", "aria-label": label, dense: true }}
      >
        {options.map((option) => {
          const selected = option.value === value.value;
          return (
            <MenuItem
              key={option.value}
              role="option"
              selected={selected}
              aria-selected={selected}
              onClick={() => {
                setAnchorEl(null);
                if (!selected) {
                  onChange(option.value);
                }
              }}
              sx={{ gap: 1 }}
            >
              {selected ? <Check sx={{ fontSize: 16 }} /> : <Box sx={{ width: 16 }} />}
              <SkillAccessChip token={option} />
              <ListItemText
                primary={option.description}
                primaryTypographyProps={{ variant: "body2", color: "text.secondary" }}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
